<script lang="ts">
	import { navigation, trackpadMotion } from '$lib/aqueduct/navigation';
	import { IS_AQUEDUCT } from '$lib/site';
	import { onDestroy } from 'svelte';
	import '$lib/aqueduct/viewportCanvas.css';
	import { AqueductCameraControls, type AqueductCameraState } from '$lib/aqueductCameraControls';
	import {
		getDefaultMaterialGridSsaaScale,
		getEffectiveMaterialGridSsaaScale,
		getMaterialViewportDpr
	} from '$lib/surfaceDpr';
	import type { ProjectHandle } from '$lib/aqueduct/engine/projectHandle';
	import type { MaterialDisplayMode, MaterialViewportParams } from '$lib/aqueduct/engine/protocol';
	import type { MaterialOutputRole } from '$lib/aqueduct/engine/broker';
	import type { MaterialViewMode } from '$lib/aqueduct/materialViewMode';
	import {
		DEFAULT_MATERIAL_BLOOM_STRENGTH,
		DEFAULT_MATERIAL_DISPLACEMENT_SCALE
	} from '$lib/aqueduct/materialRendererDefaults';

	export type MaterialRole =
		| 'albedo'
		| 'height'
		| 'normal'
		| 'roughness'
		| 'metallic'
		| 'ao'
		| 'opacity';
	export type ViewMode = MaterialViewMode;
	export type RenderMode = 'interactive' | 'preview';
	export type SourceMode = 'graph-outputs' | 'external-textures';
	export type SsaaScale = 1 | 2 | 3 | 4 | 6 | 8;
	export type DisplacementTriangleBudget = 50000 | 100000 | 200000 | 400000 | 800000;
	type LegacyMapType =
		| 'map'
		| 'displacementMap'
		| 'normalMap'
		| 'metalnessMap'
		| 'roughnessMap'
		| 'aoMap'
		| 'opacityMap';

	type MaterialViewportTiming = { avg: number; count: number; triangles: number };
	type CameraBenchmarkReport = {
		durationMs: number;
		requestedFrames: number;
		materialFrames: number;
		workerFrames: number;
		materialFps: number;
		workerFps: number;
		cadenceMs: { median: number; p95: number; max: number };
		longFramesOver25Ms: number;
	};

	let {
		wasmProject,
		sourceMode = 'graph-outputs',
		viewMode,
		displayMode = 'material',
		renderMode = 'interactive',
		isDark,
		displacementScale = DEFAULT_MATERIAL_DISPLACEMENT_SCALE,
		displacementTriangleBudget = 100000,
		showTriangleEdges = false,
		bloom = true,
		bloomStrength = DEFAULT_MATERIAL_BLOOM_STRENGTH,
		ssaaScale = getDefaultMaterialGridSsaaScale(),
		enableMomentum = false,
		initialCameraState = null,
		initialEnvironmentYaw = 0,
		onCameraChange = null,
		onEnvironmentYawChange = null,
		revealed = null,
		fitPixelPadding = null,
		colorMap = $bindable(null),
		heightMap = $bindable(null),
		normalMap = $bindable(null),
		metallicMap = $bindable(null),
		roughnessMap = $bindable(null),
		aoMap = $bindable(null),
		opacityMap = $bindable(null)
	}: {
		wasmProject: ProjectHandle | null;
		sourceMode?: SourceMode;
		viewMode: ViewMode;
		displayMode?: MaterialDisplayMode;
		renderMode?: RenderMode;
		isDark: boolean;
		displacementScale?: number;
		displacementTriangleBudget?: DisplacementTriangleBudget;
		showTriangleEdges?: boolean;
		bloom?: boolean;
		bloomStrength?: number;
		ssaaScale?: SsaaScale;
		/** Key light as renderer JSON, `null` for none (editor development
		 *  builds only); undefined keeps the renderer's default key light. */
		enableMomentum?: boolean;
		initialCameraState?: Partial<AqueductCameraState> | null;
		initialEnvironmentYaw?: number;
		onCameraChange?: ((state: AqueductCameraState) => void) | null;
		onEnvironmentYawChange?: ((yaw: number) => void) | null;
		/** Parent-controlled reveal beat (null keeps the self-managed
		 *  first-present gate, e.g. the standalone visualizer). */
		revealed?: boolean | null;
		/** When set, the default zoom is computed from the canvas geometry so
		 *  the sphere's silhouette clears the constraining dimension by this
		 *  many CSS pixels on each side. Ignored when an initial camera state
		 *  supplies a radius. */
		fitPixelPadding?: number | null;
		colorMap?: HTMLImageElement | null;
		heightMap?: HTMLImageElement | null;
		normalMap?: HTMLImageElement | null;
		metallicMap?: HTMLImageElement | null;
		roughnessMap?: HTMLImageElement | null;
		aoMap?: HTMLImageElement | null;
		opacityMap?: HTMLImageElement | null;
	} = $props();

	let canvasEl: HTMLCanvasElement = $state(undefined!);
	let resizeObserver: ResizeObserver | null = null;
	let registered: boolean = $state(false);
	let registeredProject: ProjectHandle | null = null;
	let unsubscribe: (() => void) | null = null;
	let canvasWidth = 0;
	let canvasHeight = 0;
	let canvasCssWidth = 0;
	let canvasCssHeight = 0;
	let canvasDpr: number = $state(1);
	let lastSourceMode: SourceMode | null = null;
	let pendingExternalImages: Map<MaterialRole, HTMLImageElement> = new Map();
	/** Per-role generation counter: rasterizing an HTMLImageElement is async, so
	 *  a slow decode must never overwrite a newer upload or a removal. */
	let externalUploadSeq: Map<MaterialRole, number> = new Map();
	let hasRendered = $state(false);
	let liveTriangleCount: number | null = $state(null);
	let lastViewportDiagnosticsKey = '';
	const triangleCountFormatter = new Intl.NumberFormat();

	const DEFAULT_CAMERA_YAW = 0.5585993;
	const DEFAULT_CAMERA_PITCH = 0.401247;
	const DEFAULT_CAMERA_RADIUS = 5.1234756;
	const FULL_ROTATION = Math.PI * 2;

	function wrapEnvironmentYaw(yaw: number): number {
		if (!Number.isFinite(yaw)) return 0;
		return ((((yaw + Math.PI) % FULL_ROTATION) + FULL_ROTATION) % FULL_ROTATION) - Math.PI;
	}

	function defaultCameraState(): AqueductCameraState {
		return {
			yaw: DEFAULT_CAMERA_YAW,
			pitch: DEFAULT_CAMERA_PITCH,
			radius: DEFAULT_CAMERA_RADIUS,
			target: [0, 0, 0]
		};
	}

	function createCameraControls(): AqueductCameraControls {
		const defaults = defaultCameraState();
		return new AqueductCameraControls({
			yaw: initialCameraState?.yaw ?? defaults.yaw,
			pitch: initialCameraState?.pitch ?? defaults.pitch,
			radius: initialCameraState?.radius ?? defaults.radius,
			target: initialCameraState?.target,
			enableMomentum,
			rotateSpeed: 1.0
		});
	}

	function initialEnvironmentYawState(): number {
		return wrapEnvironmentYaw(initialEnvironmentYaw);
	}

	const cameraControls = createCameraControls();
	let environmentYaw = initialEnvironmentYawState();
	let lastNotifiedCamera: AqueductCameraState | null = null;
	let lastNotifiedEnvironmentYaw: number | null = null;

	function notifyCameraChange(state: AqueductCameraState): void {
		if (!onCameraChange) return;
		if (
			lastNotifiedCamera &&
			lastNotifiedCamera.yaw === state.yaw &&
			lastNotifiedCamera.pitch === state.pitch &&
			lastNotifiedCamera.radius === state.radius &&
			lastNotifiedCamera.target?.every(
				(value: number, index: number) => value === state.target?.[index]
			)
		) {
			return;
		}
		lastNotifiedCamera = state;
		onCameraChange(state);
	}

	function notifyEnvironmentYawChange(): void {
		if (!onEnvironmentYawChange || lastNotifiedEnvironmentYaw === environmentYaw) return;
		lastNotifiedEnvironmentYaw = environmentYaw;
		onEnvironmentYawChange(environmentYaw);
	}
	let renderQueued = false;
	let renderCancelled = false;
	let momentumFrame: number | null = null;

	function sizeCanvas(): boolean {
		if (!canvasEl || !canvasEl.parentElement) return false;
		const rect = canvasEl.parentElement.getBoundingClientRect();
		const dpr = getMaterialViewportDpr(rect.width, rect.height);
		const width = Math.max(1, Math.round(rect.width * dpr));
		const height = Math.max(1, Math.round(rect.height * dpr));
		canvasCssWidth = rect.width;
		canvasCssHeight = rect.height;
		canvasDpr = dpr;
		if (width === canvasWidth && height === canvasHeight) return true;
		canvasWidth = width;
		canvasHeight = height;
		if (registered) {
			// The canvas transferred to the worker at attach; the worker owns the
			// backing store and re-renders with its stored params.
			wasmProject?.resizeSurface('materialViewport', rect.width, rect.height, dpr);
		} else {
			canvasEl.width = width;
			canvasEl.height = height;
		}
		return true;
	}

	function applySourceMode(): void {
		if (!wasmProject || lastSourceMode === sourceMode) return;
		wasmProject.setMaterialViewportSourceMode(sourceMode);
		lastSourceMode = sourceMode;
	}

	function currentParams(transparentBackgroundOverride?: boolean): MaterialViewportParams {
		const cameraState = cameraControls.getState();
		const effectiveSsaaScale: SsaaScale = getEffectiveMaterialGridSsaaScale(
			ssaaScale,
			canvasWidth,
			canvasHeight
		);
		return {
			yaw: cameraState.yaw,
			pitch: cameraState.pitch,
			radius: cameraState.radius,
			target: cameraState.target,
			environmentYaw,
			viewMode,
			displayMode,
			renderMode,
			darkMode: isDark,
			// Only the worker-side screenshot path renders transparent.
			transparentBackground: transparentBackgroundOverride ?? false,
			bloom,
			bloomStrength,
			displacementScale,
			displacementTriangleBudget,
			showTriangleEdges,
			ssaaScale: effectiveSsaaScale
		};
	}

	function renderNow(): void {
		if (!registered || !wasmProject) return;
		applySourceMode();
		const params = currentParams();
		logViewportDiagnostics(params.ssaaScale);
		wasmProject.renderMaterialViewport(params);
		notifyCameraChange(cameraControls.getState());
		notifyEnvironmentYawChange();
	}

	function logViewportDiagnostics(effectiveSsaaScale: SsaaScale): void {
		const renderedPixels = canvasWidth * canvasHeight * effectiveSsaaScale * effectiveSsaaScale;
		const key = [
			Math.round(canvasCssWidth),
			Math.round(canvasCssHeight),
			canvasDpr,
			canvasWidth,
			canvasHeight,
			ssaaScale,
			effectiveSsaaScale,
			renderedPixels
		].join(':');
		if (key === lastViewportDiagnosticsKey) return;
		lastViewportDiagnosticsKey = key;
		console.log('[Aqueduct MaterialViewport size]', {
			cssWidth: Number(canvasCssWidth.toFixed(2)),
			cssHeight: Number(canvasCssHeight.toFixed(2)),
			dpr: canvasDpr,
			backingWidth: canvasWidth,
			backingHeight: canvasHeight,
			ssaaScale,
			effectiveSsaaScale,
			renderedPixels
		});
	}

	// Microtask, not rAF: an rAF defer here parked every camera/param change a
	// full main-thread frame before its mvRender message even left; a microtask
	// still coalesces same-flush bursts and the worker rate-bounds the rest.
	function requestRender(): void {
		renderCancelled = false;
		if (renderQueued) return;
		renderQueued = true;
		queueMicrotask(() => {
			renderQueued = false;
			if (renderCancelled) {
				renderCancelled = false;
				return;
			}
			renderNow();
		});
	}

	function cancelQueuedRender(): void {
		if (renderQueued) renderCancelled = true;
	}

	function cancelMomentumAnimation(): void {
		if (momentumFrame !== null) {
			cancelAnimationFrame(momentumFrame);
			momentumFrame = null;
		}
		cameraControls.cancelMomentum();
		wasmProject?.setWorkInteraction('material_viewport', false);
	}

	function runMomentumFrame(): void {
		momentumFrame = null;
		if (!registered || !wasmProject) {
			cameraControls.cancelMomentum();
			return;
		}
		const momentum = cameraControls.advanceMomentum(performance.now());
		if (momentum.changed) renderNow();
		if (momentum.active) {
			momentumFrame = requestAnimationFrame(runMomentumFrame);
		} else {
			wasmProject?.setWorkInteraction('material_viewport', false);
		}
	}

	function requestMomentumAnimation(): void {
		if (momentumFrame !== null) return;
		momentumFrame = requestAnimationFrame(runMomentumFrame);
	}

	/** Camera distance that projects the sphere's silhouette to fill the
	 *  constraining canvas dimension minus the requested padding. The mesh is
	 *  unit-radius (displacement pushes peaks outward, so it joins the
	 *  radius) and the renderer uses a 45 degree vertical FOV; a sphere at
	 *  distance d subtends asin(R/d), which projects to the same pixel
	 *  radius on both axes. */
	function fitRadiusForPadding(padding: number): number {
		const fovY = (45 * Math.PI) / 180;
		const constraint = Math.min(canvasCssWidth, canvasCssHeight);
		const targetPx = Math.max(constraint / 2 - padding, 8);
		const alpha = Math.atan((targetPx * Math.tan(fovY / 2)) / (canvasCssHeight / 2));
		const worldRadius = 1 + displacementScale;
		return worldRadius / Math.sin(alpha);
	}

	function tryRegister(): void {
		if (!canvasEl || !wasmProject || registered) return;
		if (!sizeCanvas()) return;
		if (fitPixelPadding !== null && initialCameraState?.radius === undefined) {
			cameraControls.setState({
				...cameraControls.getState(),
				radius: fitRadiusForPadding(fitPixelPadding)
			});
		}
		try {
			// The worker orders registration behind the project's engine init, so
			// no readiness gating is needed here.
			wasmProject.attachCanvas(
				{
					kind: 'materialViewport',
					width: canvasCssWidth,
					height: canvasCssHeight,
					dpr: canvasDpr
				},
				canvasEl
			);
		} catch (e) {
			console.error('material viewport canvas registration failed:', e);
			return;
		}
		registered = true;
		registeredProject = wasmProject;
		// The opacity gate flips on the worker's first-present notice: only then
		// has a real frame reached the (transferred) canvas.
		unsubscribe = wasmProject.subscribe({
			onPresented: (kinds) => {
				if (kinds.includes('materialViewport')) hasRendered = true;
			},
			onMaterialViewportTriangles: (triangles) => {
				liveTriangleCount = Math.max(0, Math.round(triangles));
			}
		});
		applySourceMode();
		flushPendingExternalImages();
		requestRender();
	}

	/** The Rust core's canonical role name for a component-level role. */
	function wasmRole(role: MaterialRole): MaterialOutputRole {
		return role === 'albedo' ? 'base_color' : role;
	}

	function normalizeRole(role: MaterialRole | LegacyMapType): MaterialRole {
		switch (role) {
			case 'map':
				return 'albedo';
			case 'displacementMap':
				return 'height';
			case 'normalMap':
				return 'normal';
			case 'metalnessMap':
				return 'metallic';
			case 'roughnessMap':
				return 'roughness';
			case 'opacityMap':
				return 'opacity';
			case 'aoMap':
				return 'ao';
			default:
				return role;
		}
	}

	function setBoundImage(role: MaterialRole, image: HTMLImageElement | null): void {
		switch (role) {
			case 'albedo':
				colorMap = image;
				break;
			case 'height':
				heightMap = image;
				break;
			case 'normal':
				normalMap = image;
				break;
			case 'metallic':
				metallicMap = image;
				break;
			case 'roughness':
				roughnessMap = image;
				break;
			case 'opacity':
				opacityMap = image;
				break;
			case 'ao':
				aoMap = image;
				break;
		}
	}

	function uploadExternalImage(role: MaterialRole, image: HTMLImageElement): void {
		if (!wasmProject) {
			pendingExternalImages.set(role, image);
			return;
		}
		pendingExternalImages.delete(role);
		applySourceMode();
		const seq = (externalUploadSeq.get(role) ?? 0) + 1;
		externalUploadSeq.set(role, seq);
		void rasterizeAndUpload(role, image, seq);
	}

	async function rasterizeAndUpload(
		role: MaterialRole,
		image: HTMLImageElement,
		seq: number
	): Promise<void> {
		try {
			// The engine lives in a worker, so an HTMLImageElement cannot cross the
			// boundary; rasterize to RGBA bytes main-side and send those.
			const bitmap = await createImageBitmap(image);
			const offscreen = new OffscreenCanvas(bitmap.width, bitmap.height);
			const ctx = offscreen.getContext('2d')!;
			ctx.drawImage(bitmap, 0, 0);
			const data = ctx.getImageData(0, 0, offscreen.width, offscreen.height).data;
			bitmap.close();
			if (externalUploadSeq.get(role) !== seq || !wasmProject) return;
			wasmProject.setMaterialViewportExternalTexture(
				wasmRole(role),
				offscreen.width,
				offscreen.height,
				new Uint8Array(data.buffer)
			);
			requestRender();
		} catch (error) {
			console.error('Failed to upload external material image:', error);
		}
	}

	function flushPendingExternalImages(): void {
		if (pendingExternalImages.size === 0) return;
		for (const [role, image] of Array.from(pendingExternalImages.entries())) {
			uploadExternalImage(role, image);
		}
	}

	export function refresh(): void {
		requestRender();
	}

	export function resetCamera(): void {
		cancelMomentumAnimation();
		environmentDragLastX = null;
		cameraControls.setState(defaultCameraState());
		environmentYaw = 0;
		requestRender();
	}

	export function renderBenchmarkFrame(options?: {
		viewMode?: ViewMode;
		renderMode?: RenderMode;
		bloom?: boolean;
		bloomStrength?: number;
		displacementScale?: number;
		displacementTriangleBudget?: DisplacementTriangleBudget;
		showTriangleEdges?: boolean;
		ssaaScale?: SsaaScale;
	}): void {
		if (!registered || !wasmProject) return;
		cancelQueuedRender();
		applySourceMode();
		const cameraState = cameraControls.getState();
		const effectiveSsaaScale: SsaaScale = getEffectiveMaterialGridSsaaScale(
			options?.ssaaScale ?? ssaaScale,
			canvasWidth,
			canvasHeight
		);
		wasmProject.renderMaterialViewport({
			yaw: cameraState.yaw,
			pitch: cameraState.pitch,
			radius: cameraState.radius,
			target: cameraState.target,
			environmentYaw,
			viewMode: options?.viewMode ?? viewMode,
			displayMode,
			renderMode: options?.renderMode ?? renderMode,
			darkMode: isDark,
			transparentBackground: false,
			bloom: options?.bloom ?? bloom,
			bloomStrength: options?.bloomStrength ?? bloomStrength,
			displacementScale: options?.displacementScale ?? displacementScale,
			displacementTriangleBudget: options?.displacementTriangleBudget ?? displacementTriangleBudget,
			showTriangleEdges: options?.showTriangleEdges ?? false,
			ssaaScale: effectiveSsaaScale,
		});
	}

	export function renderBenchmarkCameraFrame(
		elapsedSeconds: number,
		options?: Parameters<typeof renderBenchmarkFrame>[0]
	): void {
		cameraControls.setState({
			yaw: DEFAULT_CAMERA_YAW + elapsedSeconds * 0.8,
			pitch: DEFAULT_CAMERA_PITCH + Math.sin(elapsedSeconds * 0.7) * 0.18,
			radius: DEFAULT_CAMERA_RADIUS
		});
		renderBenchmarkFrame(options);
	}

	export function readRenderTiming(): Promise<MaterialViewportTiming | null> {
		return wasmProject?.readMaterialViewportTiming() ?? Promise.resolve(null);
	}

	export async function runCameraBenchmark(durationMs = 60000): Promise<CameraBenchmarkReport> {
		if (!registered || !wasmProject) throw new Error('Material viewport is not ready.');
		const duration = Math.max(1000, Math.floor(durationMs));
		cancelMomentumAnimation();
		cancelQueuedRender();
		const original = cameraControls.getState();
		const startDebug = await wasmProject.timeDebug();
		const startedAt = performance.now();
		let previous = startedAt;
		const cadence: number[] = [];
		while (performance.now() - startedAt < duration) {
			const now = await new Promise<number>((resolve) => requestAnimationFrame(resolve));
			cadence.push(now - previous);
			previous = now;
			const elapsedSeconds = (now - startedAt) * 0.001;
			cameraControls.setState({
				yaw: original.yaw + elapsedSeconds * 0.8,
				pitch: original.pitch + Math.sin(elapsedSeconds * 0.7) * 0.18
			});
			renderNow();
		}
		await new Promise<number>((resolve) => requestAnimationFrame(resolve));
		await new Promise<number>((resolve) => requestAnimationFrame(resolve));
		const endDebug = await wasmProject.timeDebug();
		cameraControls.setState(original);
		renderNow();
		const elapsed = Math.max(1, endDebug.workerNowMs - startDebug.workerNowMs);
		const sorted = [...cadence].sort((a, b) => a - b);
		const percentile = (fraction: number): number =>
			sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction))] ?? 0;
		const materialFrames = endDebug.materialRenderCount - startDebug.materialRenderCount;
		const workerFrames = endDebug.frameWorkPasses - startDebug.frameWorkPasses;
		return {
			durationMs: elapsed,
			requestedFrames: cadence.length,
			materialFrames,
			workerFrames,
			materialFps: (materialFrames * 1000) / elapsed,
			workerFps: (workerFrames * 1000) / elapsed,
			cadenceMs: {
				median: percentile(0.5),
				p95: percentile(0.95),
				max: sorted.at(-1) ?? 0
			},
			longFramesOver25Ms: cadence.filter((sample) => sample > 25).length
		};
	}

	export async function takeScreenshot(): Promise<void> {
		if (!registered || !wasmProject) return;
		// The canvas is transferred, so pixel reads happen in the worker: it
		// renders with a transparent background, crops the transparent border,
		// returns the PNG bytes, and restores the normal view automatically.
		const png = await wasmProject.surfaceScreenshot('materialViewport', currentParams(true));
		if (!png) return;
		const url = URL.createObjectURL(new Blob([png], { type: 'image/png' }));
		const link = document.createElement('a');
		link.download = `aqueduct-material-${viewMode}-${displayMode.replace('_', '-')}.png`;
		link.href = url;
		link.click();
		URL.revokeObjectURL(url);
	}

	export function updateMaterialMap(
		roleOrMapType: MaterialRole | LegacyMapType,
		image: HTMLImageElement
	): void {
		const role = normalizeRole(roleOrMapType);
		setBoundImage(role, image);
		uploadExternalImage(role, image);
	}

	export function removeMap(roleOrMapType: MaterialRole | 'color' | 'metallic'): void {
		const role =
			roleOrMapType === 'color'
				? 'albedo'
				: roleOrMapType === 'metallic'
					? 'metallic'
					: normalizeRole(roleOrMapType as MaterialRole | LegacyMapType);
		pendingExternalImages.delete(role);
		externalUploadSeq.set(role, (externalUploadSeq.get(role) ?? 0) + 1);
		setBoundImage(role, null);
		wasmProject?.clearMaterialViewportExternalTexture(wasmRole(role));
		requestRender();
	}

	export function clearPBRMaps(): void {
		for (const role of [
			'height',
			'normal',
			'metallic',
			'roughness',
			'ao',
			'opacity'
		] as MaterialRole[]) {
			removeMap(role);
		}
	}

	let environmentDragLastX: number | null = null;

	function handlePointerDown(event: PointerEvent): void {
		event.preventDefault();
		cancelMomentumAnimation();
		wasmProject?.setWorkInteraction('material_viewport', true);
		if (event.button === 2 && event.shiftKey) {
			environmentDragLastX = event.clientX;
			canvasEl.setPointerCapture(event.pointerId);
			return;
		}
		if (!cameraControls.beginPointerDrag(event.clientX, event.clientY, performance.now())) return;
		canvasEl.setPointerCapture(event.pointerId);
	}

	function handlePointerMove(event: PointerEvent): void {
		if (environmentDragLastX !== null) {
			const dx = event.clientX - environmentDragLastX;
			environmentDragLastX = event.clientX;
			if (dx === 0) return;
			environmentYaw = wrapEnvironmentYaw(
				environmentYaw + (dx * FULL_ROTATION) / Math.max(1, canvasCssHeight)
			);
			requestRender();
			return;
		}
		const changed = cameraControls.movePointerDrag(
			event.clientX,
			event.clientY,
			canvasCssHeight,
			performance.now()
		);
		if (!changed) return;
		requestRender();
	}

	function handlePointerUp(event: PointerEvent): void {
		if (environmentDragLastX !== null) {
			wasmProject?.setWorkInteraction('material_viewport', false);
			environmentDragLastX = null;
			if (canvasEl.hasPointerCapture(event.pointerId)) {
				canvasEl.releasePointerCapture(event.pointerId);
			}
			return;
		}
		const hasMomentum = cameraControls.endPointerDrag(performance.now());
		if (canvasEl.hasPointerCapture(event.pointerId)) {
			canvasEl.releasePointerCapture(event.pointerId);
		}
		if (hasMomentum) requestMomentumAnimation();
		else wasmProject?.setWorkInteraction('material_viewport', false);
	}

	function handleWheel(event: WheelEvent): void {
		event.preventDefault();
		cancelMomentumAnimation();
		if (IS_AQUEDUCT && $navigation.mode === 'trackpad') {
			const motion = trackpadMotion(event, $navigation.mac, canvasCssHeight);
			if (motion.dx === 0 && motion.dy === 0) return;
			const changed: boolean = motion.zoom
				? cameraControls.zoomByWheel(motion.dy, 0, true)
				: motion.shift
					? cameraControls.panByScroll(motion.dx, motion.dy, canvasCssHeight)
					: cameraControls.orbitByScroll(motion.dx, motion.dy, canvasCssHeight);
			if (!changed) return;
		} else if (!cameraControls.zoomByWheel(event.deltaY, event.deltaMode, event.ctrlKey)) return;
		requestRender();
	}

	$effect(() => {
		// Changing preference stops old inertia without resetting or reinterpreting a drag.
		$navigation.preference;
		if (IS_AQUEDUCT) cancelMomentumAnimation();
	});

	function getTouchDistance(touches: TouchList): number {
		const firstTouch = touches[0];
		const secondTouch = touches[1];
		return Math.hypot(
			secondTouch.clientX - firstTouch.clientX,
			secondTouch.clientY - firstTouch.clientY
		);
	}

	function handleTouchStart(event: TouchEvent): void {
		// Single-finger orbits must suppress native selection as well as pinches.
		event.preventDefault();
		if (event.touches.length !== 2) return;
		event.stopPropagation();
		cancelMomentumAnimation();
		cameraControls.beginPinch(getTouchDistance(event.touches));
	}

	function handleTouchMove(event: TouchEvent): void {
		event.preventDefault();
		if (event.touches.length !== 2) return;
		event.stopPropagation();
		if (!cameraControls.movePinch(getTouchDistance(event.touches))) return;
		requestRender();
	}

	function handleTouchEnd(event: TouchEvent): void {
		cameraControls.endPinch(event.touches.length);
	}

	function teardown(): void {
		unsubscribe?.();
		unsubscribe = null;
		if (registered) registeredProject?.detachSurface('materialViewport');
		registered = false;
		registeredProject = null;
	}

	function canvasAction(node: HTMLCanvasElement) {
		canvasEl = node;
		resizeObserver = new ResizeObserver(() => {
			sizeCanvas();
			requestRender();
		});
		if (node.parentElement) {
			resizeObserver.observe(node.parentElement);
		}
		node.addEventListener('wheel', handleWheel, { passive: false });
		node.addEventListener('touchstart', handleTouchStart, { passive: false });
		node.addEventListener('touchmove', handleTouchMove, { passive: false });
		node.addEventListener('touchend', handleTouchEnd);
		node.addEventListener('touchcancel', handleTouchEnd);
		tryRegister();
		return {
			destroy() {
				node.removeEventListener('wheel', handleWheel);
				node.removeEventListener('touchstart', handleTouchStart);
				node.removeEventListener('touchmove', handleTouchMove);
				node.removeEventListener('touchend', handleTouchEnd);
				node.removeEventListener('touchcancel', handleTouchEnd);
				resizeObserver?.disconnect();
				resizeObserver = null;
				teardown();
			}
		};
	}

	$effect(() => {
		if (wasmProject && canvasEl && !registered) {
			tryRegister();
		}
		if (wasmProject) {
			applySourceMode();
			flushPendingExternalImages();
		}
	});

	$effect(() => {
		if (!showTriangleEdges) liveTriangleCount = null;
	});

	$effect(() => {
		viewMode;
		displayMode;
		renderMode;
		isDark;
		displacementScale;
		displacementTriangleBudget;
		showTriangleEdges;
		bloom;
		bloomStrength;
		ssaaScale;
		sourceMode;
		if (registered && wasmProject) {
			applySourceMode();
			requestRender();
		}
	});

	onDestroy(() => {
		cancelQueuedRender();
		cancelMomentumAnimation();
		resizeObserver?.disconnect();
		resizeObserver = null;
		teardown();
	});
</script>

<div
	class="aqueduct-material-viewport relative h-full w-full overflow-hidden"
	style:--canvas-dpr={canvasDpr}
>
	<canvas
		use:canvasAction
		class={`aqueduct-unscaled-canvas block cursor-grab transition-opacity duration-[200ms] active:cursor-grabbing ${revealed ?? hasRendered ? 'opacity-100' : 'opacity-0'}`}
		style="touch-action: none;"
		onpointerdown={handlePointerDown}
		onpointermove={handlePointerMove}
		onpointerup={handlePointerUp}
		onpointercancel={handlePointerUp}
		oncontextmenu={(event) => event.preventDefault()}
	></canvas>
	{#if showTriangleEdges && liveTriangleCount !== null}
		<div
			class="material-triangle-count pointer-events-none absolute bottom-1 left-1 rounded border px-1.5 py-0.5 text-[10px] leading-none font-medium tabular-nums shadow-sm backdrop-blur-sm"
			class:material-triangle-count-dark={isDark}
		>
			{triangleCountFormatter.format(liveTriangleCount)} triangles
		</div>
	{/if}
</div>

<style>
	/* Both values must equal the engine's clear_color (material_renderer
	   clear_color: light 0.96/0.955/0.88, dark 16/16/22): this container is
	   what shows before the canvas's first frame fades in, so any difference
	   appears as a color jump at mount and again at reveal. */
	.aqueduct-material-viewport {
		-webkit-user-select: none;
		user-select: none;
		-webkit-touch-callout: none;
		background-color: #f5f4e0;
	}

	:global(.dark) .aqueduct-material-viewport {
		background-color: #101016;
	}

	.material-triangle-count {
		background-color: rgba(250, 250, 247, 0.96);
		border-color: #d4d4d8;
		color: #27272a;
	}

	.material-triangle-count-dark {
		background-color: rgba(14, 14, 18, 0.96);
		border-color: #27272f;
		color: #d4d4d8;
	}
</style>
